import { Controller } from '@hotwired/stimulus'

export default class extends Controller {
  static targets = [
    'triggerButton',
    'modal',
    'modalBody',
    'greeting',
    'optionButtons',
    'chatBox',
    'chatInterface',
    'chatInputContainer',
    'chatInput',
    'chatMessages',
    'conversationHistory',
    'historyList',
  ]

  connect() {
    // Use the global ActionCable consumer if available
    this.consumer = window.CampusESP?.cable || null
    // Debounce timer for markdown rendering
    this.renderTimer = null
    // Current conversation state (includes conversation_token, id, and title)
    this.currentConversation = null
    // Current ActionCable subscription
    this.currentSubscription = null
  }

  disconnect() {
    // Clean up any active subscription
    this.cleanupSubscription()
  }

  // Clean up the current ActionCable subscription
  cleanupSubscription() {
    if (this.currentSubscription) {
      this.currentSubscription.unsubscribe()
      this.currentSubscription = null
    }
  }

  // After two seconds the AI support button will collapse
  triggerButtonTargetConnected() {
    setTimeout(() => {
      this.triggerButtonTarget.classList.add('user-support-collapsing')
    }, 2000)
  }

  openModal() {
    this.modalTarget.classList.add('modal-open')
    document.body.classList.add('modal-active')
  }

  closeModal() {
    this.modalTarget.classList.remove('modal-open')
    document.body.classList.remove('modal-active')
  }

  startChat() {
    // Clear current conversation when starting a new chat
    this.currentConversation = null
    this.toggleChat('start')
  }

  restartChat() {
    // Clear current conversation when starting a new chat
    this.currentConversation = null
    this.toggleChat('reset')
    this.toggleChat('start')
  }

  openOldConversations() {
    // Hide other views
    this.optionButtonsTarget.style.display = 'none'
    this.chatInterfaceTarget.style.display = 'none'
    this.chatInputContainerTarget.style.display = 'none'
    this.greetingTarget.style.display = 'none'
    this.chatBoxTarget.style.display = 'none'

    // Show conversation history
    this.conversationHistoryTarget.style.display = 'block'

    // Reset to loading state
    this.historyListTarget.innerHTML = `
      <div class="loading-history">
        <span class="fas fa-spinner fa-spin"></span>
        Loading conversations...
      </div>
    `

    // Load conversation history
    this.loadConversationHistory()
  }

  toggleChat(mode = 'start') {
    // Show greeting message and update text
    this.greetingTarget.style.display = 'block'
    this.greetingTarget.textContent =
      mode === 'start'
        ? 'Great! If you have a question, I have an answer!'
        : 'Hi! How can I help you today?'

    // Toggle option buttons (opposite of chat state)
    this.optionButtonsTarget.style.display = mode === 'start' ? 'none' : 'block'

    // Toggle chat interface
    this.chatInterfaceTarget.style.display = mode === 'start' ? 'block' : 'none'
    this.chatBoxTarget.style.display = mode === 'start' ? 'block' : 'none'

    // Toggle input container
    this.chatInputContainerTarget.style.display =
      mode === 'start' ? 'flex' : 'none'

    // Hide conversation history when switching to other views
    this.conversationHistoryTarget.style.display = 'none'

    // Clear messages and message text input when closing modal
    if (mode === 'reset') {
      this.chatInputTarget.value = ''
      this.autoResizeTextarea()
      if (this.chatMessagesTarget) {
        this.chatMessagesTarget.innerHTML = ''
      }
    }
  }

  // Handle Enter key press for sending messages
  handleEnterKey(event) {
    // Only handle Enter key, not Shift + Enter (which adds new line)
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault()
      this.sendMessage()
    }
  }

  // Auto-resize textarea based on content
  autoResizeTextarea() {
    const textarea = this.chatInputTarget
    const chatBox = this.chatBoxTarget

    // Check if user is at the bottom before resizing
    const isAtBottom =
      chatBox.scrollTop + chatBox.clientHeight >= chatBox.scrollHeight - 10

    // Reset height to auto to get the correct scrollHeight
    textarea.style.height = 'auto'

    // Set the height to match the scroll height,
    // with a max height of 120 pixels and a min height of 43 pixels
    const maxHeight = 120 // Maximum height in pixels (about 5-6 lines)
    const newHeight = Math.min(Math.max(textarea.scrollHeight, 43), maxHeight)

    textarea.style.height = newHeight + 'px'

    // Add scrollbar if content exceeds max height
    textarea.style.overflowY =
      textarea.scrollHeight > maxHeight ? 'auto' : 'hidden'

    // Update chat-box margin to match input container height
    chatBox.style.marginBottom = newHeight + 40 + 'px'

    // Jump to bottom of chatbox if we were already at the bottom
    if (isAtBottom) {
      chatBox.scrollTop = chatBox.scrollHeight
    }
  }

  sendMessage() {
    const message = this.chatInputTarget.value.trim()

    if (message) {
      // Add user message bubble and clear input
      this.addMessage(message, 'user')
      this.chatInputTarget.value = ''

      // Reset textarea height after send a message
      this.autoResizeTextarea()

      // Show thinking animation
      this.showThinkingAnimation()

      // Create ActionCable subscription which will trigger the job
      this.createChatbotSubscription(message)
    }
  }

  // Create subscription which triggers the job and receives the response
  createChatbotSubscription(message) {
    if (!this.consumer) {
      console.error('ActionCable consumer not available')
      return
    }

    // Clean up any existing subscription to prevent duplicates
    this.cleanupSubscription()

    const self = this
    let currentBotMessage = null

    // Always generate a unique session_id for this ActionCable subscription
    const sessionId = crypto.randomUUID()

    // Pass conversation_token if continuing an existing conversation, otherwise null
    const conversationToken =
      this.currentConversation?.conversation_token || null

    this.currentSubscription = this.consumer.subscriptions.create(
      {
        channel: 'ChatbotChannel',
        session_id: sessionId,
        conversation_token: conversationToken,
        message: message,
      },
      {
        received(response) {
          const responseText = response['response']

          if (responseText === 'Response completed') {
            // Final response - render any remaining buffered content immediately
            if (currentBotMessage) {
              clearTimeout(self.renderTimer)
              self.renderMarkdown(currentBotMessage)
            }
            currentBotMessage = null
          } else if (
            responseText &&
            responseText.startsWith('CONVERSATION_SUMMARY:')
          ) {
            // Handle conversation summary display
            const summaryJson = responseText.replace(
              'CONVERSATION_SUMMARY: ',
              '',
            )
            if (summaryJson.trim()) {
              try {
                const summary = JSON.parse(summaryJson)

                // Update current conversation with data from backend
                // This ensures we have the correct conversation_token for future messages
                if (summary.conversation_token) {
                  if (!self.currentConversation) {
                    self.currentConversation = {
                      conversation_token: summary.conversation_token,
                      id: summary.conversation_id,
                      title: 'New Conversation',
                    }
                  } else {
                    // Update existing conversation data
                    self.currentConversation.conversation_token =
                      summary.conversation_token
                    self.currentConversation.id = summary.conversation_id
                  }
                }

                // Build sources list HTML for hover popup
                const sourcesCount =
                  summary.sources_used && summary.sources_used.length > 0
                    ? summary.sources_used.length
                    : 0
                const sourcesPopupHtml =
                  sourcesCount > 0
                    ? self.buildSourcesPopupHtml(summary.sources_used)
                    : ''
                const tokensUsed = summary.tokens_used || 0

                const summaryElement = document.createElement('div')
                summaryElement.className = 'conversation-summary fade-in'
                summaryElement.innerHTML = `
                  ${
                    sourcesCount > 0
                      ? `
                  <div class="sources-pill-container">
                    <div class="sources-pill" data-action="click->user-support#toggleSourcesPopup">
                      <span class="fas fa-book"></span>
                      ${sourcesCount} source${sourcesCount !== 1 ? 's' : ''}
                    </div>
                    <div class="sources-popup">
                      <div class="sources-popup-header">
                        Sources | Tokens: ${tokensUsed.toLocaleString()}
                      </div>
                      ${sourcesPopupHtml}
                    </div>
                  </div>
                  `
                      : ''
                  }
                `
                self.chatMessagesTarget.appendChild(summaryElement)
              } catch (e) {
                console.error('Failed to parse conversation summary JSON:', e)
              }
            }
          } else if (responseText) {
            // Streaming content chunk - append to current bot message
            if (!currentBotMessage) {
              // Hide thinking animation when first bot message starts
              self.hideThinkingAnimation()

              // Create new bot message bubble
              currentBotMessage = document.createElement('div')
              currentBotMessage.className = 'message-bubble fade-in'
              currentBotMessage.dataset.markdownBuffer = '' // Store raw markdown
              self.chatMessagesTarget.appendChild(currentBotMessage)
            }

            // Append the chunk to the markdown buffer
            currentBotMessage.dataset.markdownBuffer += responseText

            // Debounce the markdown rendering to avoid partial markdown syntax
            clearTimeout(self.renderTimer)
            self.renderTimer = setTimeout(() => {
              self.renderMarkdown(currentBotMessage)
            }, 50) // 50ms debounce - fast enough to feel real-time, slow enough to batch chunks
          }
        },
      },
    )
  }

  // Helper to render markdown in a message bubble
  renderMarkdown(bubble) {
    const markdownText = bubble.dataset.markdownBuffer
    if (window.marked && markdownText) {
      bubble.innerHTML = window.marked.parse(markdownText, { breaks: true })
    } else {
      bubble.textContent = markdownText || ''
    }
  }

  // Helper to scroll to the bottom of the modal
  scrollToBottom() {
    this.chatBoxTarget.scrollTo({
      top: this.chatBoxTarget.scrollHeight,
      behavior: 'smooth',
    })
  }

  // Creates and displays a chat message bubble for the given text and sender type
  addMessage(text, sender) {
    const bubble = document.createElement('div')
    bubble.className =
      sender === 'user'
        ? 'message-bubble user-message fade-in'
        : 'message-bubble fade-in'

    // User messages are plain text, bot messages may contain markdown
    if (sender === 'user') {
      bubble.innerHTML = text.replace(/\n/g, '<br>')
    } else {
      bubble.dataset.markdownBuffer = text
      this.renderMarkdown(bubble)
    }

    this.chatMessagesTarget.appendChild(bubble)
    this.scrollToBottom()
  }

  // Show thinking animation
  showThinkingAnimation() {
    // Remove any existing thinking animation
    this.hideThinkingAnimation()

    const thinkingElement = document.createElement('div')
    thinkingElement.id = 'thinking-animation'
    thinkingElement.className = 'message-bubble thinking-animation fade-in'
    thinkingElement.innerHTML = `
      <div class="thinking-indicator">
        <span class="thinking-text">Thinking</span>
        <div class="thinking-dots">
          <div class="thinking-dot"></div>
          <div class="thinking-dot"></div>
          <div class="thinking-dot"></div>
        </div>
      </div>
    `

    // Add CSS animation if not already added
    if (!document.getElementById('thinking-animation-style')) {
      const style = document.createElement('style')
      style.id = 'thinking-animation-style'
      style.textContent = `
        @keyframes thinking {
          0%, 80%, 100% {
            transform: scale(0.8);
            opacity: 0.5;
          }
          40% {
            transform: scale(1);
            opacity: 1;
          }
        }
      `
      document.head.appendChild(style)
    }

    this.chatMessagesTarget.appendChild(thinkingElement)
    this.scrollToBottom()
  }

  // Hide thinking animation
  hideThinkingAnimation() {
    const thinkingElement = document.getElementById('thinking-animation')
    if (thinkingElement) {
      thinkingElement.remove()
    }
  }

  // Close modal when clicking outside the content area
  modalBackdropClick(event) {
    if (event.target === this.modalTarget) {
      this.closeModal()
    }
  }

  // Load conversation history from the server
  async loadConversationHistory() {
    try {
      const response = await fetch('/api/v1/ai_conversations', {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': document
            .querySelector('meta[name="csrf-token"]')
            ?.getAttribute('content'),
        },
      })

      if (!response.ok) {
        throw new Error('Failed to load conversations')
      }

      const conversations = await response.json()
      this.renderConversationHistory(conversations)
    } catch (error) {
      console.error('Error loading conversations:', error)

      this.historyListTarget.innerHTML = `
        <div class="conversation-notice text-danger">
          <span class="fas fa-exclamation-triangle"></span>
          Failed to load conversations
        </div>
      `
    }
  }

  // Render the conversation history list
  renderConversationHistory(conversations) {
    if (conversations.length === 0) {
      this.historyListTarget.innerHTML = `
        <div class="conversation-notice text-muted">
          <span class="fas fa-comment"></span>
          No previous conversations found.
        </div>
      `
      return
    }

    const conversationItems = conversations
      .map((conversation) => {
        const title = conversation.title || 'Untitled Conversation'

        return `
        <div class="conversation-item"
             data-conversation-id="${conversation.id}"
             data-action="click->user-support#loadConversationFromHistory">
          <div class="conversation-content">
            <div class="conversation-title" role="link" tabindex="0">${this.escapeHtml(title)}</div>
          </div>
          <div class="conversation-arrow">
            <span class="fas fa-chevron-right"></span>
          </div>
        </div>
      `
      })
      .join('')

    this.historyListTarget.innerHTML = conversationItems
  }

  // Stimulus action to load conversation from history item click
  loadConversationFromHistory(event) {
    const conversationId = event.currentTarget.dataset.conversationId
    if (conversationId) {
      this.loadConversation(conversationId)
    }
  }

  // Load a specific conversation and display its messages
  async loadConversation(conversationId) {
    // Show loading state
    this.historyListTarget.innerHTML = `
      <div class="loading-history">
        <span class="fas fa-spinner fa-spin"></span>
        Loading conversation...
      </div>
    `

    try {
      const response = await fetch(
        `/api/v1/ai_conversations/${conversationId}`,
        {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            'X-CSRF-Token': document
              .querySelector('meta[name="csrf-token"]')
              ?.getAttribute('content'),
          },
        },
      )

      if (!response.ok) {
        throw new Error('Failed to load conversation')
      }

      const conversation = await response.json()
      this.displayLoadedConversation(conversation)
    } catch (error) {
      console.error('Error loading conversation:', error)
      // Show error message to user
      this.historyListTarget.innerHTML = `
        <div class="conversation-notice text-danger">
          <span class="fas fa-exclamation-triangle"></span>
          Failed to load conversation
        </div>
      `
    }
  }

  // Display the loaded conversation messages and switch to chat interface
  displayLoadedConversation(conversation) {
    // Store current conversation data
    this.currentConversation = conversation

    // Switch to chat interface
    this.toggleChat('start')

    // Clear existing messages
    this.chatMessagesTarget.innerHTML = ''

    // Display all messages from the conversation
    conversation.messages.forEach((message) => {
      if (message.role === 'user') {
        this.addMessage(message.content, 'user')
      } else if (message.role === 'assistant') {
        this.addMessage(message.content, 'assistant')

        // Add sources if available
        if (message.sources_used && message.sources_used.length > 0) {
          this.addSourcesPill(message.sources_used, message.tokens_used || 0)
        }
      }
    })

    // Keep the regular greeting when loading a conversation
    this.greetingTarget.textContent =
      'Great! If you have a question, I have an answer!'

    // Scroll to bottom to show latest messages
    this.scrollToBottom()
  }

  // Helper method to build sources popup HTML
  buildSourcesPopupHtml(sources) {
    let html = '<div class="sources-popup-list">'
    sources.forEach((source) => {
      const title = this.escapeHtml(source.title || 'Unknown Source')
      const sourceType = source.source_type || 'unknown'
      const score = source.score ? Number(source.score).toFixed(2) : '0.00'
      const url = source.url

      // Make the entire source item clickable if URL is available
      if (url) {
        html += `
          <a href="${url}" target="_blank" rel="noopener noreferrer" class="source-item source-link">
            <div class="source-title">${title}</div>
            <div class="source-meta">${sourceType} • Score: ${score}</div>
          </a>
        `
      } else {
        html += `
          <div class="source-item">
            <div class="source-title">${title}</div>
            <div class="source-meta">${sourceType} • Score: ${score}</div>
          </div>
        `
      }
    })
    html += '</div>'
    return html
  }

  // Add sources pill to show sources used for an assistant message
  addSourcesPill(sources, tokensUsed = 0) {
    const sourcesCount = sources.length
    const sourcesPopupHtml = this.buildSourcesPopupHtml(sources)

    const summaryElement = document.createElement('div')
    summaryElement.className = 'conversation-summary fade-in'
    summaryElement.innerHTML = `
      <div class="sources-pill-container">
        <div class="sources-pill" data-action="click->user-support#toggleSourcesPopup">
          <span class="fas fa-book"></span>
          ${sourcesCount} source${sourcesCount !== 1 ? 's' : ''}
        </div>
        <div class="sources-popup">
          <div class="sources-popup-header">
            Sources | Tokens: ${tokensUsed.toLocaleString()}
          </div>
          ${sourcesPopupHtml}
        </div>
      </div>
    `
    this.chatMessagesTarget.appendChild(summaryElement)
  }

  // Helper method to escape HTML
  escapeHtml(text) {
    const div = document.createElement('div')
    div.textContent = text
    return div.innerHTML
  }

  // Toggle sources popup visibility when sources pill is clicked
  toggleSourcesPopup(event) {
    const container = event.currentTarget.closest('.sources-pill-container')
    if (container) {
      container.classList.toggle('active-mobile')
    }
  }

  // Close the modal and open the Pendo (Intercom) guide
  openPendoGuide() {
    this.closeModal()
    if (window.pendo && window.pendo.guides[1].canShow()) {
      window.pendo.guides[1].show()
    } else {
      console.log('Cannot open guide')
    }
  }
}
